import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { FieldsetModule } from 'primeng/fieldset';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AppUser, BranchSummary, BrandSummary, BusinessSummary, BusinessUnitSummary, CustomerSummary, SupplierSummary } from '../user.model';
import { UserService } from '../users.service';
import { RolesService } from '../../master-data/roles/roles.service';
import { BranchesService } from '../../master-data/branches/branches.service';
import { BrandsService } from '../../master-data/brands/brands.service';
import { BusinessUnitsService } from '../../master-data/business-units/business-units.service';
import { BusinessService } from '../../master-data/business/business.service';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Page } from '../../models/page-wrapper.model';
import { Role } from '../../master-data/roles/role.model';

interface StatusOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    AutoCompleteModule,
    CheckboxModule,
    FieldsetModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    IftaLabelModule,
    RouterModule,
    RadioButtonModule,
    SelectModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.css'],
})
export class EditUserComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  userForm!: FormGroup;
  pageTitle = 'Create User';
  userId: string | null = null;

  roles: Role[] = [];
  branchSuggestions: BranchSummary[] = [];
  brandSuggestions: BrandSummary[] = [];
  businessUnitSuggestions: BusinessUnitSummary[] = [];
  businessSuggestions: BusinessSummary[] = [];
  supplierSuggestions: SupplierSummary[] = [];
  customerSuggestions: CustomerSummary[] = [];
  isCustomersLoading = false;
  isBranchLoading = false;
  isBrandLoading = false;
  isBusinessUnitLoading = false;
  isBusinessLoading = false;
  isSupplierLoading = false;

  readonly statusOptions: StatusOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Pending', value: 'pending_verification' },
    { label: 'Deactivated', value: 'deactivated' },
  ];

  private currentUser: AppUser | null = null;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private rolesService: RolesService,
    private branchesService: BranchesService,
    private brandsService: BrandsService,
    private businessUnitsService: BusinessUnitsService,
    private businessService: BusinessService,
    private suppliersService: SuppliersService,
    private customersService: CustomersService,
    private messageService: MessageService,
  ) {
    this.userForm = this.fb.group({
      id: [null],
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      status: ['active', Validators.required],
      mobileNumber: [''],
      roleId: [null, Validators.required],
      branchSelections: [[], Validators.required],
      businessUnitSelections: [[], Validators.required],
      businessSelections: [[], Validators.required],
      brandSelections: [[], Validators.required],
      supplierSelections: [[], Validators.required],
      customerSelections: [[]],
      canChangeRebate: [false],
      canChangeClaim: [false],
    });
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.userId && this.userId !== 'create';
    this.pageTitle = this.isEditMode ? 'Edit User' : 'Create User';

    this.loadPageData();
  }

  private loadPageData(): void {
    this.isLoading = true;

    forkJoin({
      roles: this.rolesService.getAllRoles({ page: 0, size: 2000 }).pipe(
        map((page: Page<Role>) => page.content ?? [])
      ),
      user: this.isEditMode && this.userId ? this.userService.getUserById(+this.userId) : of(null),
    }).subscribe({
      next: (result) => {
        this.roles = result.roles;

        if (result.user) {
          this.currentUser = result.user;
          this.patchFormValue(result.user);
          this.hydrateSelectionsFromIds(result.user);
        } else {
          this.currentUser = null;
        }

        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user editor data.',
        });
        console.error(err);
      },
    });
  }

  private patchFormValue(user: AppUser): void {
    this.userForm.patchValue({
      id: user.id ?? null,
      fullName: user.fullName ?? '',
      email: user.email ?? '',
      mobileNumber: user.mobileNumber ?? '',
      status: user.status ?? 'active',
      roleId: this.normalizeId(user.roles?.[0]?.id ?? null),
      branchSelections: [],
      businessUnitSelections: [],
      businessSelections: [],
      brandSelections: [],
      supplierSelections: [],
      customerSelections: [],
      canChangeRebate: !!user.canChangeRebate,
      canChangeClaim: !!user.canChangeClaim,
    });
  }

  private hydrateSelectionsFromIds(user: AppUser): void {
    this.hydrateBusinessUnitSelections(user.businessUnitIds ?? []);
    this.hydrateBusinessSelections(user.businessIds ?? []);
    this.hydrateBranchSelections(user.branchIds ?? []);
    this.hydrateBrandSelections(user.brandIds ?? []);
    this.hydrateSupplierSelections(user.supplierIds ?? []);
    this.hydrateCustomerSelections(user.customerIds ?? []);
  }

  private hydrateBranchSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.branchesService.getBranchById(id), (items) => {
      const selections = items.map((item) => this.toBranchSummary(item));
      this.branchSuggestions = selections;
      this.userForm.get('branchSelections')?.setValue(selections);
    });
  }

  private hydrateBusinessUnitSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.businessUnitsService.getBusinessUnitById(id), (items) => {
      const selections = items.map((item) => this.toBusinessUnitSummary(item));
      this.businessUnitSuggestions = selections;
      this.userForm.get('businessUnitSelections')?.setValue(selections);
    });
  }

  private hydrateBusinessSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.businessService.getBusinessById(id), (items) => {
      const selections = items.map((item) => this.toBusinessSummary(item));
      this.businessSuggestions = selections;
      this.userForm.get('businessSelections')?.setValue(selections);
    });
  }

  private hydrateBrandSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.brandsService.getBrandById(id), (items) => {
      const selections = items.map((item) => this.toBrandSummary(item));
      this.brandSuggestions = selections;
      this.userForm.get('brandSelections')?.setValue(selections);
    });
  }

  private hydrateSupplierSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.suppliersService.getSupplierById(id), (items) => {
      const selections = items.map((item) => this.toSupplierSummary(item));
      this.supplierSuggestions = selections;
      this.userForm.get('supplierSelections')?.setValue(selections);
    });
  }

  private hydrateCustomerSelections(ids: number[]): void {
    this.hydrateSelectionList(ids, (id) => this.customersService.getCustomerById(id), (items) => {
      const selections = items.map((item) => this.toCustomerSummary(item));
      this.customerSuggestions = selections;
      this.userForm.get('customerSelections')?.setValue(selections);
    });
  }

  private hydrateSelectionList<T>(
    ids: number[],
    getById: (id: number) => Observable<T>,
    setValue: (items: T[]) => void
  ): void {
    const validIds = ids
      .map((id) => this.normalizeId(id))
      .filter((id): id is number => Number.isFinite(id));
    if (!validIds.length) {
      return;
    }

    forkJoin(validIds.map((id) => getById(id))).subscribe({
      next: (items) => setValue(items),
      error: (err) => console.error(err),
    });
  }

  private normalizeId(value: unknown): number | null {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private toBranchSummary(branch: { id: number; name?: string; code?: string }): BranchSummary {
    return {
      id: branch.id,
      name: `${branch.name ?? ''}${branch.code ? ` - ${branch.code}` : ''}`.trim(),
    };
  }

  private toBusinessUnitSummary(businessUnit: { id: number; businessUnitName?: string; businessUnitCode?: string }): BusinessUnitSummary {
    return {
      id: businessUnit.id,
      name: `${businessUnit.businessUnitName ?? ''}${businessUnit.businessUnitCode ? ` - ${businessUnit.businessUnitCode}` : ''}`.trim(),
    };
  }

  private toBusinessSummary(business: { id: number; businessName?: string; businessCode?: string }): BusinessSummary {
    return {
      id: business.id,
      name: `${business.businessName ?? ''}${business.businessCode ? ` - ${business.businessCode}` : ''}`.trim(),
    };
  }

  private toBrandSummary(brand: { id: number; brandName?: string; brandCode?: string }): BrandSummary {
    return {
      id: brand.id,
      name: `${brand.brandName ?? ''}${brand.brandCode ? ` - ${brand.brandCode}` : ''}`.trim(),
    };
  }

  private toSupplierSummary(supplier: { id: number; supplierName?: string; supplierNumber?: string }): SupplierSummary {
    return {
      id: supplier.id,
      name: `${supplier.supplierName ?? ''}${supplier.supplierNumber ? ` - ${supplier.supplierNumber}` : ''}`.trim(),
    };
  }

  private toCustomerSummary(customer: { id: number; customerNumber?: string; customerName?: string }): CustomerSummary {
    return {
      id: customer.id,
      customerNumber: customer.customerNumber ?? '',
      customerName: customer.customerName ?? '',
      name: `${customer.customerNumber ?? ''}${customer.customerName ? ` - ${customer.customerName}` : ''}`.trim(),
    };
  }

  private normalizeString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  private normalizeIds(value: unknown): number[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((entry) => {
        if (typeof entry === 'object' && entry !== null && 'id' in entry) {
          return Number((entry as { id: unknown }).id);
        }

        return Number(entry);
      })
      .filter((entry) => Number.isFinite(entry));
  }

  selectAllBranches(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.loadAllBranches().subscribe((items) => {
      this.branchSuggestions = items;
      this.userForm.get('branchSelections')?.setValue(items);
      this.userForm.get('branchSelections')?.markAsDirty();
    });
  }

  selectAllBusinessUnits(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.loadAllBusinessUnits().subscribe((items) => {
      this.businessUnitSuggestions = items;
      this.userForm.get('businessUnitSelections')?.setValue(items);
      this.userForm.get('businessUnitSelections')?.markAsDirty();
    });
  }

  selectAllBusiness(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.loadAllBusiness().subscribe((items) => {
      this.businessSuggestions = items;
      this.userForm.get('businessSelections')?.setValue(items);
      this.userForm.get('businessSelections')?.markAsDirty();
    });
  }

  selectAllBrands(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.loadAllBrands().subscribe((items) => {
      this.brandSuggestions = items;
      this.userForm.get('brandSelections')?.setValue(items);
      this.userForm.get('brandSelections')?.markAsDirty();
    });
  }

  selectAllSuppliers(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.loadAllSuppliers().subscribe((items) => {
      this.supplierSuggestions = items;
      this.userForm.get('supplierSelections')?.setValue(items);
      this.userForm.get('supplierSelections')?.markAsDirty();
    });
  }

  selectAllCustomers(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isCustomersLoading = true;
    this.loadAllCustomerSummaries()
      .pipe(
        finalize(() => {
          this.isCustomersLoading = false;
        })
      )
      .subscribe({
        next: (customers) => {
          this.userForm.get('customerSelections')?.setValue(customers);
          this.userForm.get('customerSelections')?.markAsDirty();
          this.customerSuggestions = customers;
        },
        error: (err) => {
          console.error(err);
        },
    });
  }

  searchBranches(event: { query?: string }): void {
    this.searchEntity(event?.query, (query) => this.branchesService.searchBranches(query, 0, 25), (items) => {
      this.branchSuggestions = items.map((item) => this.toBranchSummary(item));
    }, () => (this.isBranchLoading = true), () => (this.isBranchLoading = false));
  }

  searchBusinessUnits(event: { query?: string }): void {
    this.searchEntity(event?.query, (query) => this.businessUnitsService.searchBusinessUnits(query, 0, 25), (items) => {
      this.businessUnitSuggestions = items.map((item) => this.toBusinessUnitSummary(item));
    }, () => (this.isBusinessUnitLoading = true), () => (this.isBusinessUnitLoading = false));
  }

  searchBusiness(event: { query?: string }): void {
    this.searchEntity(event?.query, (query) => this.businessService.searchBusinesses(query, 0, 25), (items) => {
      this.businessSuggestions = items.map((item) => this.toBusinessSummary(item));
    }, () => (this.isBusinessLoading = true), () => (this.isBusinessLoading = false));
  }

  searchBrands(event: { query?: string }): void {
    this.searchEntity(event?.query, (query) => this.brandsService.searchBrands(query, 0, 25), (items) => {
      this.brandSuggestions = items.map((item) => this.toBrandSummary(item));
    }, () => (this.isBrandLoading = true), () => (this.isBrandLoading = false));
  }

  searchSuppliers(event: { query?: string }): void {
    this.searchEntity(event?.query, (query) => this.suppliersService.searchSuppliers(query, 0, 25), (items) => {
      this.supplierSuggestions = items.map((item) => this.toSupplierSummary(item));
    }, () => (this.isSupplierLoading = true), () => (this.isSupplierLoading = false));
  }

  searchCustomers(event: { query?: string }): void {
    const query = (event?.query ?? '').trim();
    if (!query) {
      this.customerSuggestions = [];
      return;
    }

    this.isCustomersLoading = true;
    this.customersService
      .searchCustomers(query, 0, 25)
      .pipe(
        map((page) => (page.content ?? []).map((customer) => this.toCustomerSummary(customer))),
        finalize(() => {
          this.isCustomersLoading = false;
        })
      )
      .subscribe({
        next: (customers) => {
          this.customerSuggestions = customers;
        },
        error: (err) => {
          console.error(err);
          this.customerSuggestions = [];
        },
      });
  }

  private searchEntity<T>(
    query: string | undefined,
    searchFn: (query: string) => Observable<Page<T>>,
    assign: (items: T[]) => void,
    setLoading: () => void,
    clearLoading: () => void
  ): void {
    const normalized = (query ?? '').trim();
    if (!normalized) {
      assign([]);
      clearLoading();
      return;
    }

    setLoading();
    searchFn(normalized)
      .pipe(finalize(() => clearLoading()))
      .subscribe({
        next: (page) => assign(page.content ?? []),
        error: (err) => {
          console.error(err);
          assign([]);
        },
      });
  }

  private loadAllBranches(): Observable<BranchSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.branchesService.getAllBranches({ page, size }),
      (item: { id: number; name?: string; code?: string }) => this.toBranchSummary(item)
    );
  }

  private loadAllBusinessUnits(): Observable<BusinessUnitSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.businessUnitsService.getAllBusinessUnits({ page, size }),
      (item: { id: number; businessUnitName?: string; businessUnitCode?: string }) => this.toBusinessUnitSummary(item)
    );
  }

  private loadAllBusiness(): Observable<BusinessSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.businessService.getAllBusinesss({ page, size }),
      (item: { id: number; businessName?: string; businessCode?: string }) => this.toBusinessSummary(item)
    );
  }

  private loadAllBrands(): Observable<BrandSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.brandsService.getAllBrands({ page, size }),
      (item: { id: number; brandName?: string; brandCode?: string }) => this.toBrandSummary(item)
    );
  }

  private loadAllSuppliers(): Observable<SupplierSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.suppliersService.getAllSuppliers({ page, size }),
      (item: { id: number; supplierName?: string; supplierNumber?: string }) => this.toSupplierSummary(item)
    );
  }

  private loadAllPaged<T, S>(
    loader: (page: number, size: number) => Observable<Page<T>>,
    mapper: (item: T) => S
  ): Observable<S[]> {
    const pageSize = 1000;

    return loader(0, pageSize).pipe(
      switchMap((firstPage) => {
        const totalPages = firstPage.totalPages ?? 1;
        if (totalPages <= 1) {
          return of([firstPage]);
        }

        const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => loader(index + 1, pageSize));
        return forkJoin([of(firstPage), ...remainingPages]);
      }),
      map((pages: Page<T>[]) => pages.flatMap((page) => page.content ?? []).map((item) => mapper(item)))
    );
  }

  private loadAllCustomerSummaries(): Observable<CustomerSummary[]> {
    return this.loadAllPaged(
      (page, size) => this.customersService.getAllCustomers({ page, size }),
      (item: { id: number; customerNumber?: string; customerName?: string }) => this.toCustomerSummary(item)
    );
  }

  saveUser(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const formValue = this.userForm.getRawValue();
    const fullName = this.normalizeString(formValue.fullName);
    const email = this.normalizeString(formValue.email);

    if (!fullName || !email) {
      this.userForm.markAllAsTouched();
      return;
    }

    const roleId = Number(formValue.roleId);
    const mappedRoles = Number.isFinite(roleId)
      ? this.roles.filter((role) => role.id === roleId)
      : [];

    const userData: AppUser = {
      ...(this.currentUser ?? {}),
      id: this.isEditMode ? +this.userId! : undefined,
      fullName,
      email,
      mobileNumber: this.normalizeString(formValue.mobileNumber),
      status: formValue.status,
      roles: mappedRoles,
      branchIds: this.normalizeIds(formValue.branchSelections),
      businessUnitIds: this.normalizeIds(formValue.businessUnitSelections),
      businessIds: this.normalizeIds(formValue.businessSelections),
      brandIds: this.normalizeIds(formValue.brandSelections),
      supplierIds: this.normalizeIds(formValue.supplierSelections),
      customerIds: this.normalizeIds(formValue.customerSelections),
      canChangeRebate: !!formValue.canChangeRebate,
      canChangeClaim: !!formValue.canChangeClaim,
    };

    this.isSaving = true;

    const request$ = this.isEditMode
      ? this.userService.updateUser(userData)
      : this.userService.createUser(userData);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `User ${this.isEditMode ? 'updated' : 'created'} successfully.`,
        });
        setTimeout(() => this.cancel(), 500);
      },
      error: (err) => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} user.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['users', 'list'] } },
    ]);
  }
}
